import React, { useMemo } from 'react';
import {
  Autocomplete,
  Box,
  TextField,
  Typography,
} from '@mui/material';
import type {
  RepairServiceType,
  RepairDeviceType,
} from '@techgitter/contracts';
import { useRepairServiceTypes } from '../../hooks/useRepairServiceTypes';

export interface RepairServicePickerProps {
  /** Currently-selected RCS.* code, or empty string for "not chosen". */
  value: string;
  /** Hardware form-factor filter — null shows everything. Device-independent
   *  rows (deviceType=null) always appear regardless of this filter. */
  deviceType?: RepairDeviceType;
  onPick: (type: RepairServiceType) => void;
  disabled?: boolean;
  size?: 'small' | 'medium';
}

/**
 * Searchable dropdown of repair-service types fetched from the catalog.
 */
export const RepairServicePicker: React.FC<RepairServicePickerProps> = ({
  value,
  deviceType,
  onPick,
  disabled = false,
  size = 'small',
}) => {
  const { types, isLoading, error } = useRepairServiceTypes();

  // Group heading for a catalog row. Device-independent rows (deviceType
  // null) fall under a single "Device Independent" group; device-scoped
  // rows use the form factor with underscores spaced out and the initial
  // letter capitalised (e.g. `all_in_one` → "All in one").
  const groupLabel = (t: RepairServiceType): string => {
    if (!t.deviceType) return 'Device Independent';
    const spaced = t.deviceType.replace(/_/g, ' ');
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  };

  const filtered = useMemo(() => {
    const scoped = deviceType
      ? types.filter(
          (t) => t.deviceType === deviceType || t.deviceType == null,
        )
      : types;
    // MUI's Autocomplete `groupBy` only renders one header per group when
    // the options are pre-sorted by that group key — otherwise it emits a
    // fresh header each time the group value changes while walking the
    // list, so the options read as ungrouped. Sort by group label, then
    // by service name within each group for a stable order.
    return [...scoped].sort((a, b) => {
      const ga = groupLabel(a);
      const gb = groupLabel(b);
      if (ga !== gb) return ga.localeCompare(gb);
      return (a.serviceType || a.repairServiceCode).localeCompare(
        b.serviceType || b.repairServiceCode,
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [types, deviceType]);

  // Resolve the currently-selected catalog row so the input renders the
  // display name even when only the code is in component state (typical
  // when loading an existing quote).
  const current = types.find((t) => t.repairServiceCode === value) ?? null;

  return (
    <Autocomplete
      size={size}
      disabled={disabled || isLoading}
      options={filtered}
      value={current}
      isOptionEqualToValue={(opt, val) =>
        opt.repairServiceCode === val.repairServiceCode
      }
      getOptionLabel={(opt) =>
        opt.serviceType || opt.repairServiceCode
      }
      groupBy={groupLabel}
      onChange={(_, picked) => {
        if (picked) onPick(picked);
      }}
      renderOption={(props, opt) => (
        <Box component="li" {...props}>
          <Typography variant="body2">
            {opt.serviceType || opt.repairServiceCode}
          </Typography>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Repair service"
          error={!!error}
          helperText={error ?? undefined}
          placeholder={isLoading ? 'Loading…' : 'Pick a repair service'}
        />
      )}
      sx={{ minWidth: 280 }}
    />
  );
};
